import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, Card } from '@bravemove/ui-web';
import { adminFetch } from '../auth/apiClient';
import { useAuth } from '../auth/useAuth';
import { PageHeader } from '../components/PageHeader';
import { SecurityKeysCard } from '../components/SecurityKeysCard';

export function AccountSettingsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  if (!auth.adminUser) return null;
  const me = auth.adminUser;

  const requestPasswordReset = async () => {
    setResetError(null);
    try {
      await adminFetch('/admin/auth/forgot-password', {
        method: 'POST',
        body: { email: me.email },
      });
      setResetSent(true);
    } catch {
      // 204 always; treat as success even if network fails.
      setResetSent(true);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:account.title')} subtitle={t('admin:account.subtitle')} />

      <Card>
        <div className="space-y-3">
          <h2 className="text-h3 font-bold text-ink">{t('admin:account.profile.title')}</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13px]">
            <Detail label={t('admin:account.profile.displayName')}>{me.displayName ?? '—'}</Detail>
            <Detail label={t('admin:account.profile.email')}>
              <span className="font-mono">{me.email}</span>
            </Detail>
            <Detail label={t('admin:account.profile.role')}>{t(`admin:roles.${me.role}`)}</Detail>
            <Detail label={t('admin:account.profile.mfaEnrolled')}>
              {me.totpEnrolledAt ? formatDate(me.totpEnrolledAt, locale) : '—'}
            </Detail>
          </dl>
        </div>
      </Card>

      <Card>
        <div className="space-y-3">
          <h2 className="text-h3 font-bold text-ink">{t('admin:account.changePassword.title')}</h2>
          <p className="text-[13px] text-ink-2 leading-snug">
            {t('admin:account.changePassword.body')}
          </p>
          {resetSent ? (
            <Banner tone="success" title={t('admin:account.changePassword.successTitle')}>
              {t('admin:account.changePassword.successBody')}
            </Banner>
          ) : (
            <>
              {resetError ? <Banner tone="danger">{resetError}</Banner> : null}
              <Button variant="secondary" onClick={() => void requestPasswordReset()}>
                {t('admin:account.changePassword.submit')}
              </Button>
            </>
          )}
        </div>
      </Card>

      <SecurityKeysCard />

      {/* S-08 — the old copy claimed the forgot-password flow clears and
          re-enrolls MFA. It does not: resetPassword only sets the password
          hash and never touches totp_secret / status. The ONLY real reset
          path is a super admin's "Reset TOTP" on the Admin users page
          (PATCH /admin/users/:id { totpReset: true }), after which the next
          password login issues an enrollment token. The card now tells the
          truth, and links straight there for actors who hold the permission. */}
      <Card>
        <div className="space-y-3">
          <h2 className="text-h3 font-bold text-ink">
            {t('admin:account.mfaReset.title', {
              defaultValue: 'Reset two-factor authentication',
            })}
          </h2>
          <p className="text-[13px] text-ink-2 leading-snug">
            {t('admin:account.mfaReset.body', {
              defaultValue:
                'Self-service MFA reset is not available yet. If you lose access to both your authenticator and your security keys, a super admin must reset your MFA from the Admin users page. That removes your security keys, and you will re-enroll a new device at your next sign-in.',
            })}
          </p>
          {me.permissions.includes('admin_users:write') ? (
            <Button variant="secondary" onClick={() => navigate('/admin-users')}>
              {t('admin:account.mfaReset.openAdminUsers', { defaultValue: 'Open Admin users' })}
            </Button>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-mono uppercase tracking-wider text-ink-3">{label}</dt>
      <dd className="text-ink mt-1">{children}</dd>
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
